import { Typography, Stack } from "@mui/material";
import { TimePicker, type TimePickerProps } from "@mui/x-date-pickers";
import {
  formatDateTimeToTimeString,
  parseStringTimeToDate,
} from "Utils/DateUtils";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

interface Props<FormType extends FieldValues, TransformedFieldValues = FormType>
  extends TimePickerProps {
  name: FieldPath<FormType>;
  label?: string;
  control: Control<FormType, unknown, TransformedFieldValues>;
}

export const TimeFormInput = <T extends object, V extends object>({
  name,
  label,
  control,
  ...pickerProps
}: Props<T, V>) => (
  <Controller
    name={name}
    control={control}
    render={({ field: { onChange, value }, fieldState: { error } }) => (
      <Stack flexGrow={1}>
        {label && (
          <Typography
            component="label"
            htmlFor={name}
            sx={{ mb: 2, fontWeight: 400 }}
          >
            {label}
          </Typography>
        )}
        <TimePicker
          {...pickerProps}
          onChange={(value) => {
            const isInvalidTime = Number.isNaN(value?.getTime()); // for cases where some time segment is missing (eg. 11:mm), we want to keep the intermediate value, but the time itself is invalid
            if (isInvalidTime) {
              return value;
            }
            onChange(value ? formatDateTimeToTimeString(value) : null);
          }}
          value={value ? parseStringTimeToDate(value) : null}
          slotProps={{
            openPickerButton: { disableRipple: true },
            textField: {
              id: name,
              helperText: error ? error.message : " ",
              error: !!error,
            },
          }}
        />
      </Stack>
    )}
  />
);
